import { Link, toast } from "@heroui/react"
import { Button, InputGroup, Label, TextField } from "~/lib/heroui"
import { useEffect, useState } from "react"
import { AccountRestorePrompt } from "../components/AccountRestorePrompt"
import { AuthShell } from "../components/AuthShell"
import { ConfirmationPending } from "../components/ConfirmationPending"
import { PasswordField } from "../components/PasswordField"
import {
  AuthRequestError,
  identifierFor,
  signIn,
  storeAuthToken,
} from "../lib/auth"

type SignInPageProps = {
  confirmationError?: string
  confirmed?: boolean
  passwordReset?: boolean
  redirectTo?: string
  resetSent?: boolean
}

// Keyed by the code ConfirmationsController puts in the query string, so the
// server never has to hand the client a message to render verbatim.
const CONFIRMATION_ERRORS: Record<string, string> = {
  already_confirmed: "这个邮箱已经确认过了，直接登录即可。",
  expired: "确认链接已过期，请重新发送确认邮件。",
  invalid: "确认链接无效，请检查是否复制完整，或重新发送确认邮件。",
}

export function SignInPage({
  confirmationError,
  confirmed,
  passwordReset,
  redirectTo = "/dashboard",
  resetSent,
}: SignInPageProps) {
  const [pending, setPending] = useState(false)
  const [unconfirmedEmail, setUnconfirmedEmail] = useState<string>()
  // Held only to hand straight back to the restore endpoint, which re-checks it.
  const [deletedAccount, setDeletedAccount] = useState<{
    login: string
    password: string
    purgeAt?: string
  }>()

  const confirmationNotice = confirmationError
    ? (CONFIRMATION_ERRORS[confirmationError] ?? CONFIRMATION_ERRORS.invalid)
    : undefined

  // Everything here arrives as a query param on a redirect, so it fires once on mount
  // rather than in response to anything happening on this page.
  useEffect(() => {
    if (confirmed) toast.success("邮箱已确认，现在可以登录了")
    if (resetSent) toast.success("重置密码邮件已发送")
    if (passwordReset) toast.success("密码已更新，请使用新密码登录")
    if (confirmationNotice) toast.warning(confirmationNotice)
  }, [])

  async function onSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault()
    setUnconfirmedEmail(undefined)
    setDeletedAccount(undefined)
    setPending(true)

    const formData = new FormData(event.currentTarget)
    const login = String(formData.get("login") ?? "").trim()
    const password = String(formData.get("password") ?? "")

    try {
      const { token } = await signIn({
        identifier: identifierFor(login),
        password,
      })
      storeAuthToken(token)
      window.location.assign(redirectTo)
    } catch (err) {
      // The password was right; the account just is not confirmed yet, so send
      // them to the resend screen rather than a dead-end error.
      if (
        err instanceof AuthRequestError &&
        err.code === "unconfirmed" &&
        login.includes("@")
      ) {
        setUnconfirmedEmail(login)
      } else if (
        err instanceof AuthRequestError &&
        err.code === "pending_deletion"
      ) {
        // Also a right password: offer recovery instead of "账号或密码不正确",
        // which would leave them arguing with a password they know is correct.
        setDeletedAccount({ login, password, purgeAt: err.purgeAt })
      } else {
        toast.danger(err instanceof Error ? err.message : "账号或密码不正确")
      }
    } finally {
      setPending(false)
    }
  }

  if (deletedAccount) {
    return (
      <AuthShell subtitle="账号还在注销冷静期内" title="恢复这个账号？">
        <AccountRestorePrompt
          login={deletedAccount.login}
          onCancel={() => setDeletedAccount(undefined)}
          password={deletedAccount.password}
          purgeAt={deletedAccount.purgeAt}
          redirectTo={redirectTo}
        />
      </AuthShell>
    )
  }

  if (unconfirmedEmail) {
    return (
      <AuthShell subtitle="登录前请先完成邮箱验证" title="邮箱尚未验证">
        <ConfirmationPending
          email={unconfirmedEmail}
          message="这个账号还没有完成邮箱验证"
        />
      </AuthShell>
    )
  }

  return (
    <AuthShell subtitle="欢迎回来，继续记录孩子的成长" title="登录">
      <form className="flex flex-col gap-4 p-6" onSubmit={onSubmit}>
        <TextField isRequired name="login" type="text">
          <Label>邮箱或手机号码</Label>
          <InputGroup variant="secondary">
            <InputGroup.Input
              autoComplete="username"
              placeholder="邮箱或手机号码"
            />
          </InputGroup>
        </TextField>

        <PasswordField
          autoComplete="current-password"
          isRequired
          label="密码"
          name="password"
        />

        <div className="-mt-2 text-right">
          <Link
            className="text-primary-ink text-sm font-medium"
            href="/users/password/new"
          >
            忘记密码？
          </Link>
        </div>

        <Button fullWidth isPending={pending} type="submit">
          登录
        </Button>

        <p className="text-muted text-center text-sm">
          还没有账号？{" "}
          <Link className="text-primary-ink font-medium" href="/signup">
            注册
          </Link>
        </p>
      </form>
    </AuthShell>
  )
}
